import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Button,
    Row,
    Col,
    Select,
    DatePicker,
    Space,
    Typography,
    InputNumber,
    Checkbox,
    notification,
    Divider,
    Tabs,
    Badge,
    Descriptions,
    Empty,
    Card,
    Modal,
    Grid,
    Tag
} from 'antd';
import {
    SaveOutlined,
    CloseOutlined,
    UserOutlined,
    FileSearchOutlined,
    DoubleRightOutlined,
    CheckCircleFilled,
    InfoCircleOutlined,
    ShoppingCartOutlined,
    DeleteOutlined,
    PlusOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { orderService } from '../../services/orderService';
import { customerService } from '../../services/customerService';
import { channelPartnerService } from '../../services/channelPartnerService';

const { Option } = Select;
const { Title, Text } = Typography;

const NATURE_OPTIONS = [
    {
        key: 'supply',
        label: 'Supply of Solar Power Generating System (70% of Total Project Value)',
        percentOfTotal: 70,
        cgst: 2.5,
        sgst: 2.5,
        descTemplate: "Supply of {KW}KW Solar Power Generating System (70% of Total Project Value)"
    },
    {
        key: 'installation',
        label: 'Installation of Solar Power Generating System (30% of Total Project Value)',
        percentOfTotal: 30,
        cgst: 9,
        sgst: 9,
        descTemplate: "Installation of Solar Power Generating System (30% of Total Project Value)"
    },
    {
        key: 'mseb',
        label: 'MSEB liasoning',
        percentOfTotal: 100,
        cgst: 9,
        sgst: 9,
        descTemplate: "MSEB liasoning"
    },
    {
        key: 'other',
        label: 'Other / Custom Item',
        percentOfTotal: 0,
        cgst: 9,
        sgst: 9,
        descTemplate: ""
    }
];

// MilestoneRow: Single line elegant row
const MilestoneRow = ({ name, label, dateName, extraFieldName, extraFieldPlaceholder, extraFieldRules, extraFieldNormalize, form, isActive, disabled, skipDateValidation, isMobile }) => {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: isMobile && isActive && (dateName || extraFieldName) ? 'column' : 'row',
                alignItems: isMobile && isActive && (dateName || extraFieldName) ? 'flex-start' : 'center',
                padding: isMobile ? '10px 12px' : '12px 14px',
                marginBottom: 6,
                borderRadius: 8,
                border: '1px solid',
                borderColor: isActive ? '#d9f7be' : '#f0f0f0',
                backgroundColor: isActive ? '#f6ffed' : (disabled ? '#fafafa' : '#ffffff'),
                transition: 'all 0.2s ease',
                cursor: disabled ? 'not-allowed' : 'pointer',
                minHeight: 52,
                opacity: disabled && !isActive ? 0.6 : 1,
                gap: isMobile ? 8 : 0
            }}
            onClick={(e) => {
                if (disabled) return;
                if (!e.target.closest('.ant-picker') && !e.target.closest('.ant-checkbox-input') && !e.target.closest('.ant-input')) {
                    form.setFieldsValue({ [name]: !isActive });
                }
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <div style={{ marginRight: 12, display: 'flex', alignItems: 'center' }}>
                    <Form.Item name={name} valuePropName="checked" noStyle>
                        <Checkbox onClick={(e) => e.stopPropagation()} disabled={disabled} />
                    </Form.Item>
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                    <Text
                        style={{
                            fontSize: isMobile ? 13 : 14,
                            color: disabled ? '#bfbfbf' : (isActive ? '#237804' : '#262626'),
                            fontWeight: isActive && !disabled ? 600 : 400,
                            display: 'block',
                            lineHeight: '1.4',
                            overflow: 'hidden',
                            whiteSpace: isMobile ? 'normal' : 'nowrap',
                            textOverflow: 'ellipsis'
                        }}
                        title={label}
                    >
                        {label}
                    </Text>
                </div>

                {!isMobile && isActive && !dateName && !extraFieldName && !disabled && (
                    <CheckCircleFilled style={{ color: '#52c41a', marginLeft: 8 }} />
                )}
            </div>

            {(dateName || extraFieldName) && isActive && (
                <div style={{
                    display: 'flex',
                    gap: 8,
                    width: isMobile ? 'calc(100% - 32px)' : 'auto',
                    marginLeft: isMobile ? 32 : 12,
                    flexWrap: 'wrap',
                    marginTop: isMobile ? 4 : 0
                }}>
                    {dateName && (
                        <div style={{ width: isMobile ? '100%' : 140, maxWidth: isMobile ? 200 : 'none' }} onClick={(e) => e.stopPropagation()}>
                            <Form.Item
                                name={dateName}
                                style={{ marginBottom: 0 }}
                                rules={skipDateValidation ? [] : [
                                    { required: !disabled, message: 'Select date' },
                                    {
                                        validator: (_, value) => {
                                            if (disabled) return Promise.resolve();
                                            if (value && value.isAfter(dayjs(), 'day')) {
                                                return Promise.reject(new Error('Cannot be in future'));
                                            }
                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                            >
                                <DatePicker
                                    size="small"
                                    style={{ width: '100%', fontSize: 11 }}
                                    placeholder="Date"
                                    variant="filled"
                                    disabled={disabled}
                                    disabledDate={skipDateValidation ? null : (current) => current && current > dayjs().endOf('day')}
                                />
                            </Form.Item>
                        </div>
                    )}

                    {extraFieldName && (
                        <div style={{ width: isMobile ? '100%' : 140, maxWidth: isMobile ? 250 : 'none' }} onClick={(e) => e.stopPropagation()}>
                            <Form.Item
                                name={extraFieldName}
                                style={{ marginBottom: 0 }}
                                rules={disabled ? [] : (extraFieldRules || [{ required: true, message: 'Required' }])}
                                normalize={extraFieldNormalize}
                            >
                                <Input
                                    size="small"
                                    style={{ fontSize: 11 }}
                                    placeholder={extraFieldPlaceholder || 'Enter detail'}
                                    variant="filled"
                                    disabled={disabled}
                                />
                            </Form.Item>
                        </div>
                    )}
                </div>
            )}

            {isMobile && isActive && !dateName && !extraFieldName && !disabled && (
                <div style={{ marginLeft: 28 }}>
                    <Tag color="success" icon={<CheckCircleFilled />}>Completed</Tag>
                </div>
            )}
        </div>
    );
};

const OrderForm = ({ order, onSuccess, onCancel }) => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [channelPartners, setChannelPartners] = useState([]);
    const [consumerOptions, setConsumerOptions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [activeTab, setActiveTab] = useState('1');

    const tabSequence = ['1', 'items_tab', '2', '3'];

    // Watchers
    const projectType = Form.useWatch('projectType', form);
    const bankLoanYN = Form.useWatch('bankLoanYN', form);
    const formValues = Form.useWatch([], form) || {};
    const plantSizeKw = Form.useWatch('plantSizeKw', form);
    const orderValueFromForm = Form.useWatch('orderValue', form);
    const pStatus = Form.useWatch('projectStatus', form);

    // Robust locking logic for contract specification
    const isSpecLocked = (() => {
        if (!order) return false;

        // Check current form status or fall back to initial order status
        const currentStatus = pStatus || order.projectStatus;
        const isPastOpen = currentStatus && currentStatus !== 'Open';

        // Also check milestone flags (from both form state and original order)
        const milestoneLocked =
            formValues.registrationOnJanSamarthDone ||
            order.registrationOnJanSamarthDone ||
            formValues.installationCompleted ||
            order.installationCompleted ||
            formValues.registrationOnNPDone ||
            order.registrationOnNPDone;

        return isPastOpen || !!milestoneLocked;
    })();

    useEffect(() => {
        fetchCustomers();
        fetchChannelPartners();
    }, []);

    useEffect(() => {
        const currentItems = form.getFieldValue('items') || [];
        const updatedItems = currentItems.map(item => {
            if (item.natureKey && item.natureKey !== 'other') {
                return {
                    ...item,
                    description: getDescriptionForNature(item.natureKey, plantSizeKw)
                };
            }
            return item;
        });
        if (updatedItems.length > 0) {
            form.setFieldsValue({ items: updatedItems });
        }
    }, [plantSizeKw]);

    useEffect(() => {
        if (order) {
            const initialValues = {
                ...order,
                orderDate: dayjs(order.orderDate),
                items: order.items || [],
                registrationOnNPDate: order.registrationOnNPDate ? dayjs(order.registrationOnNPDate) : null,
                registrationOnJanSamarthDate: order.registrationOnJanSamarthDate ? dayjs(order.registrationOnJanSamarthDate) : null,
                sanctionLetterDate: order.sanctionLetterDate ? dayjs(order.sanctionLetterDate) : null,
                structureOrderedDate: order.structureOrderedDate ? dayjs(order.structureOrderedDate) : null,
                materialDispatchedDate: order.materialDispatchedDate ? dayjs(order.materialDispatchedDate) : null,
                installationCompletedDate: order.installationCompletedDate ? dayjs(order.installationCompletedDate) : null,
            };
            form.setFieldsValue(initialValues);
        } else {
            const defaultNature = NATURE_OPTIONS.find(n => n.key === 'supply');
            form.resetFields();
            form.setFieldsValue({
                orderDate: dayjs(),
                projectStatus: 'Open',
                projectType: 'IndividualPMSuryaghar',
                structureType: 'Standard',
                items: [{
                    description: getDescriptionForNature('supply', null),
                    quantity: 1,
                    rate: 0,
                    gstRate: defaultNature ? (defaultNature.cgst + defaultNature.sgst) : 0,
                    cgstRate: defaultNature ? defaultNature.cgst : 0,
                    sgstRate: defaultNature ? defaultNature.sgst : 0,
                    igstRate: 0,
                    natureKey: 'supply',
                    percentOfTotal: defaultNature ? defaultNature.percentOfTotal : 70
                }]
            });
        }
    }, [order, form]);

    const fetchCustomers = async () => {
        try {
            const response = await customerService.getAllCustomers({ limit: 1000 });
            setCustomers(response.data || []);
            if (order && response.data) {
                const cust = response.data.find(c => c.id === order.customerId);
                if (cust) {
                    setSelectedCustomer(cust);
                    prepareConsumerOptions(cust);
                }
            }
        } catch (error) {
            console.error('Failed to fetch customers', error);
        }
    };


    const fetchChannelPartners = async () => {
        try {
            const response = await channelPartnerService.getAllChannelPartners({ limit: 1000 });
            setChannelPartners(response.data || []);
        } catch (error) {
            console.error('Failed to fetch channel partners', error);
        }
    };

    const prepareConsumerOptions = (customer) => {
        const options = [];
        if (customer.consumerNo) options.push(customer.consumerNo);
        if (customer.consumerNumbers && customer.consumerNumbers.length > 0) {
            customer.consumerNumbers.forEach(cn => options.push(cn.consumerNumber));
        }
        setConsumerOptions([...new Set(options)]);
    };

    const handleCustomerChange = (customerId) => {
        const customer = customers.find(c => c.id === customerId);
        setSelectedCustomer(customer);
        form.setFieldsValue({ selectedConsumerNumber: null });
        if (customer) {
            prepareConsumerOptions(customer);
        } else {
            setConsumerOptions([]);
        }
    };

    const getDescriptionForNature = (key, kw) => {
        const opt = NATURE_OPTIONS.find(n => n.key === key);
        if (!opt) return "";
        let desc = opt.descTemplate;
        const kwVal = kw || "___";
        return desc.replace("{KW}", kwVal);
    };

    const handleNatureChange = (key, index) => {
        const opt = NATURE_OPTIONS.find(n => n.key === key);
        if (!opt) return;

        const orderValue = form.getFieldValue('orderValue') || 0;

        let rate = 0;
        if (key === 'other' || key === 'mseb') {
            rate = orderValue / 1.18;
        } else {
            const base = orderValue / 1.089;
            rate = base * (opt.percentOfTotal / 100);
        }

        const currentItems = form.getFieldValue('items');
        const updatedItems = [...currentItems];
        updatedItems[index] = {
            ...updatedItems[index],
            natureKey: key,
            description: key === 'other' ? '' : getDescriptionForNature(key, plantSizeKw),
            quantity: 1,
            rate: Math.round(rate),
            cgstRate: opt.cgst,
            sgstRate: opt.sgst,
            igstRate: 0,
            gstRate: opt.cgst + opt.sgst,
            percentOfTotal: opt.percentOfTotal
        };
        form.setFieldsValue({ items: updatedItems });
        calculateTotals();
    };


    const calculateTotals = () => {
        const items = form.getFieldValue('items') || [];
        const orderValue = parseFloat(form.getFieldValue('orderValue')) || 0;

        const updatedItems = items.map(item => {
            const qRaw = parseFloat(item.quantity);
            const rRaw = parseFloat(item.rate);
            const q = Number.isFinite(qRaw) ? qRaw : 0;
            const r = Number.isFinite(rRaw) ? rRaw : 0;

            const cgst = parseFloat(item.cgstRate) || 0;
            const sgst = parseFloat(item.sgstRate) || 0;
            const igst = parseFloat(item.igstRate) || 0;
            const g = cgst + sgst + igst;

            const amount = Math.round(q * r);
            const taxAmount = Math.round(amount * (g / 100));
            const totalAmount = amount + taxAmount;

            return {
                ...item,
                amount,
                taxAmount,
                totalAmount
            };
        });

        // If there's a small rounding difference vs contract value, absorb it into one item's GST
        // (keeps user-entered rates intact, avoids an explicit "Round Off" line item on invoices).
        if (orderValue > 0) {
            const grandTotal = updatedItems.reduce((sum, i) => sum + (i.totalAmount || 0), 0);
            const diff = Math.round(orderValue - grandTotal);

            if (diff !== 0 && Math.abs(diff) <= 5) {
                // Prefer the item with the highest totalAmount (most stable) to carry the rounding rupee(s)
                let bestIdx = -1;
                let bestTotal = -Infinity;
                for (let i = 0; i < updatedItems.length; i++) {
                    const t = updatedItems[i]?.totalAmount ?? -Infinity;
                    if (t > bestTotal) {
                        bestTotal = t;
                        bestIdx = i;
                    }
                }

                if (bestIdx >= 0) {
                    const next = [...updatedItems];
                    const current = next[bestIdx];
                    const nextTax = Math.max(0, Math.round((current.taxAmount || 0) + diff));
                    const nextTotal = Math.round((current.amount || 0) + nextTax);
                    next[bestIdx] = { ...current, taxAmount: nextTax, totalAmount: nextTotal };
                    form.setFieldsValue({ items: next });
                    return next;
                }
            }
        }

        form.setFieldsValue({ items: updatedItems });
        return updatedItems;
    };

    const milestoneGroups = [
        {
            label: 'Preparation (Milestone 1)',
            items: [
                { name: 'docsReceivedForNP', label: 'Docs Received (NP)' },
                { name: 'docsReceivedForJanSamarth', label: 'Docs Received (JanSamarth)' },
                { name: 'registrationOnNPDone', label: 'Registration Done (NP)', date: 'registrationOnNPDate', skipDateValidation: true },
                { name: 'emailIdChangeOnMSEB', label: 'Email ID Updated on MSEB' },
                { name: 'registrationOnJanSamarthDone', label: 'Registration Done (JanSamarth)', date: 'registrationOnJanSamarthDate' },
            ]
        },
        {
            label: 'Approvals & Bank (Milestone 2)',
            items: [
                {
                    name: 'sanctionLetterReceived',
                    label: 'Sanction Letter Received',
                    extraField: 'sanctionNumber',
                    placeholder: 'Sanction #',
                    date: 'sanctionLetterDate',
                    extraFieldNormalize: (val) => val?.toUpperCase(),
                    extraFieldRules: [{ required: true, message: 'Required' }]
                },
                { name: 'docsSentToBank', label: 'Docs Sent to Bank' },
                { name: 'loanAmountReceived', label: 'Loan Amount Received / Customer Advance' },
            ]
        },
        {
            label: 'Installation (Milestone 3)',
            items: [
                { name: 'structureOrdered', label: 'Structure Ordered', date: 'structureOrderedDate' },
                { name: 'materialDispatched', label: 'Material Dispatched', date: 'materialDispatchedDate' },
                { name: 'geoTaggedPhoto', label: 'GeoTagged Photo Uploaded' },
                { name: 'installationCompleted', label: 'Installation Completed', date: 'installationCompletedDate' },
            ]
        },
        {
            label: 'Grid Connection (Milestone 4)',
            items: [
                { name: 'genMeterTestReportLetter', label: 'Gen Meter Test Report Letter' },
                { name: 'genMeterTested', label: 'Gen Meter Tested' },
                { name: 'msebDocsSubmitted', label: 'MSEB Docs Submitted' },
                { name: 'releaseNoteReceived', label: 'Release Note Received' },
                { name: 'metersConnected', label: 'Meters Connected' },
                { name: 'metersPunched', label: 'Meters Punched & Checked' },
            ]
        },
        {
            label: 'Close Out (Milestone 5)',
            items: [
                { name: 'bankStatementObtained', label: 'Bank Statement Obtained' },
                { name: 'dcrCertificateReady', label: 'DCR Certificate Ready' },
                { name: 'installationDetailsFilledOnNP', label: 'Install Details on NP' },
                { name: 'inspectionApprovedByMSEB', label: 'Inspection Approved (MSEB)' },
                { name: 'appliedForSubsidy', label: 'Applied for Subsidy' },
            ]
        },
        {
            label: 'Post Installation (Milestone 6)',
            items: [
                { name: 'panelWarranty', label: 'Warranty for Panels' },
                { name: 'inverterWarranty', label: 'Warranty for Inverters' },
            ]
        }
    ];

    const performSubmit = async (values) => {
        try {
            setLoading(true);

            // Ensure totals are calculated one last time
            const finalItems = calculateTotals();
            const data = {
                ...values,
                items: finalItems
            };
            data.orderDate = values.orderDate ? values.orderDate.toISOString() : null;
            data.registrationOnNPDate = values.registrationOnNPDate ? values.registrationOnNPDate.toDate() : null;
            data.registrationOnJanSamarthDate = values.registrationOnJanSamarthDate ? values.registrationOnJanSamarthDate.toDate() : null;
            data.sanctionLetterDate = values.sanctionLetterDate ? values.sanctionLetterDate.toDate() : null;
            data.structureOrderedDate = values.structureOrderedDate ? values.structureOrderedDate.toDate() : null;
            data.materialDispatchedDate = values.materialDispatchedDate ? values.materialDispatchedDate.toDate() : null;
            data.installationCompletedDate = values.installationCompletedDate ? values.installationCompletedDate.toDate() : null;

            if (order) {
                await orderService.updateOrder(order.id, data);
            } else {
                await orderService.createOrder(data);
            }
            notification.success({ message: `Order ${order ? 'updated' : 'created'} successfully` });
            onSuccess();
        } catch (error) {
            notification.error({
                message: 'Error',
                description: error.error || error.message || 'Failed to save order'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (incomingValues) => {
        // Ant Design's onFinish only includes values for currently registered (mounted) fields.
        // Since we use Tabs, fields on inactive tabs are unregistered, but their values are preserved in the form store.
        // We use getFieldsValue(true) to get the complete state including all milestones and items.
        const values = { ...incomingValues, ...form.getFieldsValue(true) };
        const projectStatus = values.projectStatus;

        if (projectStatus === 'Closed' || projectStatus === 'Completed') {
            const totalPaid = (order?.milestones || []).reduce((sum, m) => sum + (m.amount || 0), 0);
            const balance = (values.orderValue || 0) - totalPaid;

            let warnings = [];

            if (balance > 0) {
                warnings.push(`a pending balance of ₹${balance.toLocaleString()}`);
            }

            let missingCoreTasks = 0;
            // Milestones 1 to 4 (Indices 0 to 3) are core (up to grid connected)
            for (let i = 0; i <= 3; i++) {
                missingCoreTasks += milestoneGroups[i].items.filter(item => {
                    const isJanSamarth = item.name === 'docsReceivedForJanSamarth' || item.name === 'registrationOnJanSamarthDone';
                    if (values.bankLoanYN === 'N' && isJanSamarth) return false;
                    return !values[item.name];
                }).length;
            }

            let missingPostTasks = 0;
            // Milestones 5 and 6 (Indices 4 and 5)
            for (let i = 4; i <= 5; i++) {
                missingPostTasks += milestoneGroups[i].items.filter(item => !values[item.name]).length;
            }

            if (projectStatus === 'Completed' && missingCoreTasks > 0) {
                warnings.push(`${missingCoreTasks} uncompleted core milestone tasks (M1 - M4)`);
            }

            if (projectStatus === 'Closed') {
                const totalMissing = missingCoreTasks + missingPostTasks;
                if (totalMissing > 0) {
                    warnings.push(`${totalMissing} uncompleted milestone tasks across all stages`);
                }
            }

            if (warnings.length > 0) {
                Modal.confirm({
                    centered: true,
                    title: 'Review Warning Before Proceeding',
                    content: `This order still has ${warnings.join(' AND ')}. Are you sure you want to mark it as ${projectStatus}?`,
                    okText: `Yes, Mark as ${projectStatus}`,
                    cancelText: 'Cancel',
                    okButtonProps: { danger: true },
                    onOk: () => performSubmit(values),
                });
                return;
            }
        }

        performSubmit(values);
    };

    const isTaskCompleted = (itemName) => {
        const isJanSamarth = itemName === 'docsReceivedForJanSamarth' || itemName === 'registrationOnJanSamarthDone';
        if (bankLoanYN === 'N' && isJanSamarth) return true;
        return !!formValues[itemName];
    };

    const completedCount = milestoneGroups.reduce((count, group) => {
        return count + group.items.filter(item => isTaskCompleted(item.name)).length;
    }, 0);

    const tabItems = [
        {
            key: '1',
            label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <UserOutlined /> Essentials
                </span>
            ),
            children: (
                <div style={{ padding: isMobile ? '0' : '8px 4px' }}>
                    <Row gutter={[24, 24]}>
                        <Col xs={24} lg={10}>
                            <div style={{ marginBottom: 24 }}>
                                <Title level={5} style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Badge status="processing" /> Customer Information
                                </Title>
                                <Form.Item
                                    name="customerId"
                                    rules={[{ required: true, message: 'Select customer' }]}
                                    style={{ marginBottom: 16 }}
                                >
                                    <Select
                                        showSearch
                                        placeholder="Search by Name or Mobile..."
                                        optionFilterProp="children"
                                        size="large"
                                        onChange={handleCustomerChange}
                                        style={{ width: '100%' }}
                                    >
                                        {customers.map(c => (
                                            <Option key={c.id} value={c.id}>{c.customerName} - {c.customerMobile}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                {selectedCustomer ? (
                                    <Card
                                        size="small"
                                        style={{
                                            borderRadius: 12,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                            border: '1px solid #e6f7ff',
                                            background: '#f0f9ff'
                                        }}
                                    >
                                        <Descriptions column={1} size="small" colon={false}>
                                            <Descriptions.Item label={<Text type="secondary"><UserOutlined /> Contact</Text>}>
                                                <Text strong>{selectedCustomer.customerMobile}</Text>
                                            </Descriptions.Item>
                                            <Descriptions.Item label={<Text type="secondary"><InfoCircleOutlined /> Email</Text>}>
                                                <Text>{selectedCustomer.customerEmail || 'Not Provided'}</Text>
                                            </Descriptions.Item>
                                            <Descriptions.Item label={<Text type="secondary"><InfoCircleOutlined /> Address</Text>}>
                                                <Text style={{ fontSize: 13 }}>{selectedCustomer.customerAddress || 'No Address'}</Text>
                                            </Descriptions.Item>
                                        </Descriptions>

                                        <Divider style={{ margin: '12px 0' }} />

                                        <Form.Item
                                            label={<Text strong style={{ fontSize: 13 }}>Primary Consumer Number</Text>}
                                            name="selectedConsumerNumber"
                                            rules={[{ required: true, message: 'Please select consumer number' }]}
                                            style={{ marginBottom: 4 }}
                                        >
                                            <Select
                                                placeholder="Choose consumer number"
                                                size="middle"
                                                variant="filled"
                                                allowClear
                                            >
                                                {consumerOptions.map((opt, idx) => (
                                                    <Option key={idx} value={opt}>{opt}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Card>
                                ) : (
                                    <div style={{ padding: '40px 0', textAlign: 'center', background: '#fafafa', borderRadius: 12, border: '1px dashed #d9d9d9' }}>
                                        <Empty description="Choose a customer to see quick info" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                    </div>
                                )}
                            </div>
                        </Col>

                        <Col xs={24} lg={14}>
                            <Title level={5} style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Badge status="warning" /> Order Configuration
                            </Title>
                            <Card style={{ borderRadius: 12, border: '1px solid #f0f0f0' }} bodyStyle={{ padding: isMobile ? 12 : 24 }}>
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} md={14}>
                                        <Form.Item label="Order Classification" name="projectType" rules={[{ required: true }]}>
                                            <Select size="large" variant="filled">
                                                <Option value="SocietyPMSuryaghar">Society PM Suryaghar</Option>
                                                <Option value="IndividualPMSuryaghar">Individual PM Suryaghar</Option>
                                                <Option value="Commercial">Commercial</Option>
                                                <Option value="Industrial">Industrial</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={10}>
                                        <Form.Item label="System Size" name="plantSizeKw" rules={[{ required: true, message: 'Please enter system size' }]}>
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                size="large"
                                                placeholder="kWp"
                                                variant="filled"
                                                addonAfter={!isMobile && "kW"}
                                                disabled={isSpecLocked}
                                                min={0}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label="Structure Type" name="structureType">
                                            <Select size="large" variant="filled">
                                                <Option value="Standard">Standard Mounting</Option>
                                                <Option value="Custom">Custom / Elevated</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label="Order Date" name="orderDate" rules={[{ required: true }]}>
                                            <DatePicker style={{ width: '100%', maxWidth: isMobile ? 250 : 'none' }} size="large" variant="filled" />
                                        </Form.Item>
                                    </Col>

                                    {['SocietyPMSuryaghar', 'IndividualPMSuryaghar'].includes(projectType) && (
                                        <>
                                            <Col xs={24} md={12}>
                                                <Form.Item label="Financing Mode" name="bankLoanYN" initialValue="N">
                                                    <Select size="large" variant="filled">
                                                        <Option value="Y">Bank Loan Required</Option>
                                                        <Option value="N">Self-Financed</Option>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <Form.Item label="Connection Type" name="connectionType" initialValue="SinglePhase">
                                                    <Select size="large" variant="filled">
                                                        <Option value="SinglePhase">Single Phase</Option>
                                                        <Option value="ThreePhase">Three Phase</Option>
                                                        <Option value="Mixed">Mixed</Option>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                        </>
                                    )}

                                    <Col xs={24} md={12}>
                                        <Form.Item label="Channel Partner Name" name="channelPartnerId">
                                            <Select
                                                showSearch
                                                placeholder="Select Channel Partner"
                                                optionFilterProp="children"
                                                size="large"
                                                variant="filled"
                                                prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                                                allowClear
                                            >
                                                {channelPartners.map(cp => (
                                                    <Option key={cp.id} value={cp.id}>{cp.partnerName}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Sales Agent"
                                            name="salesAgent"
                                            rules={[
                                                { pattern: /^[^0-9]*$/, message: 'Numbers are not allowed' }
                                            ]}
                                        >
                                            <Input
                                                size="large"
                                                variant="filled"
                                                placeholder="Enter agent name"
                                                prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col span={24}>
                                        <Form.Item label="Additional Remarks" name="remarks" style={{ marginBottom: 0 }}>
                                            <Input.TextArea
                                                rows={isMobile ? 2 : 3}
                                                placeholder="Any special instructions or project notes..."
                                                variant="filled"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                    </Row>
                </div>
            )
        },
        {
            key: 'items_tab',
            label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ShoppingCartOutlined /> Items & Services
                </span>
            ),
            children: (
                <div style={{ padding: isMobile ? '0' : '8px 4px' }}>
                    <Card size="small" style={{ marginBottom: 20, background: '#f9f9f9', borderRadius: 12 }}>
                        <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} lg={9}>
                                <Title level={5} style={{ margin: 0 }}>Contract Valuation</Title>
                                <Text type="secondary" style={{ fontSize: 13 }}>Agreed amount for itemization.</Text>
                            </Col>
                            <Col xs={12} lg={6}>
                                <div style={{
                                    textAlign: 'center',
                                    background: '#fff',
                                    padding: '8px 12px',
                                    borderRadius: 10,
                                    border: '1px solid #f0f0f0',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase' }}>System Size</Text>
                                    <Text strong style={{ fontSize: 16, color: '#1890ff' }}>{plantSizeKw || 0} kW</Text>
                                </div>
                            </Col>
                            <Col xs={12} lg={9}>
                                <Form.Item
                                    label={<Text strong style={{ fontSize: 12 }}>TOTAL CONTRACT VALUE</Text>}
                                    name="orderValue"
                                    rules={[{ required: true, message: 'Please enter total contract value' }]}
                                    style={{ marginBottom: 0 }}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        size={isMobile ? "middle" : "large"}
                                        variant="filled"
                                        placeholder="Enter total amount"
                                        formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        parser={value => value.replace(/\₹\s?|(,*)/g, '')}
                                        disabled={isSpecLocked}
                                        min={0}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <Form.List name="items">
                        {(fields, { add, remove }) => (
                            <>
                                {!isMobile && (
                                    <div style={{
                                        background: '#f5f5f5',
                                        padding: '12px 16px',
                                        borderRadius: '8px 8px 0 0',
                                        border: '1px solid #d9d9d9',
                                        borderBottom: 'none'
                                    }}>
                                        <Row gutter={12}>
                                            <Col span={10}><Text strong style={{ fontSize: 12 }}>Nature of Supply</Text></Col>
                                            <Col span={3} style={{ textAlign: 'center' }}><Text strong style={{ fontSize: 12 }}>CGST %</Text></Col>
                                            <Col span={3} style={{ textAlign: 'center' }}><Text strong style={{ fontSize: 12 }}>SGST %</Text></Col>
                                            <Col span={2} style={{ textAlign: 'center' }}><Text strong style={{ fontSize: 12 }}>Qty</Text></Col>
                                            <Col span={6} style={{ textAlign: 'left', paddingLeft: 8 }}><Text strong style={{ fontSize: 12 }}>Rate (₹)</Text></Col>
                                        </Row>
                                    </div>
                                )}
                                {fields.map(({ key, name, ...restField }, index) => (
                                    <div key={key} style={{
                                        padding: isMobile ? '12px' : '16px',
                                        background: '#fff',
                                        border: '1px solid #d9d9d9',
                                        borderTop: index === 0 && !isMobile ? 'none' : '1px solid #d9d9d9',
                                        marginBottom: isMobile ? 12 : 0,
                                        borderRadius: isMobile ? 8 : 0,
                                        position: 'relative'
                                    }}>
                                        <Row gutter={[12, 12]} align="top">
                                            <Col xs={24} lg={10}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'natureKey']}
                                                    rules={[{ required: true }]}
                                                    style={{ marginBottom: 2 }}
                                                    label={isMobile && <Text strong style={{ fontSize: 12 }}>Nature of Supply</Text>}
                                                >
                                                    <Select
                                                        placeholder="Select Nature"
                                                        size="middle"
                                                        onChange={(val) => handleNatureChange(val, index)}
                                                        style={{ width: '100%' }}
                                                        allowClear
                                                        disabled={isSpecLocked}
                                                    >
                                                        {NATURE_OPTIONS.map(opt => (
                                                            <Option key={opt.key} value={opt.key}>{opt.label.split('(')[0]}</Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'description']}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Input placeholder="Description" size="small" variant="borderless" style={{ color: '#8c8c8c', padding: '0 4px', fontSize: 11 }} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={8} md={6} lg={3}>
                                                <Form.Item {...restField} label={isMobile && <Text type="secondary" style={{ fontSize: 10 }}>CGST %</Text>} name={[name, 'cgstRate']} style={{ marginBottom: 0 }}>
                                                    <InputNumber style={{ width: '100%' }} precision={2} size="middle" disabled={isSpecLocked} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={8} md={6} lg={3}>
                                                <Form.Item {...restField} label={isMobile && <Text type="secondary" style={{ fontSize: 10 }}>SGST %</Text>} name={[name, 'sgstRate']} style={{ marginBottom: 0 }}>
                                                    <InputNumber style={{ width: '100%' }} precision={2} size="middle" disabled={isSpecLocked} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={8} md={4} lg={2}>
                                                <Form.Item {...restField} label={isMobile && <Text type="secondary" style={{ fontSize: 10 }}>Qty</Text>} name={[name, 'quantity']} style={{ marginBottom: 0 }}>
                                                    <InputNumber style={{ width: '100%' }} min={0.01} size="middle" disabled={isSpecLocked} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8} lg={6}>
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexDirection: 'row' }}>
                                                    <Form.Item {...restField} label={isMobile && <Text strong style={{ fontSize: 12 }}>Rate (₹)</Text>} name={[name, 'rate']} style={{ marginBottom: 0, flex: 1, width: '100%' }}>
                                                        <InputNumber style={{ width: '100%' }} precision={0} size="middle" disabled={isSpecLocked} min={0} />
                                                    </Form.Item>
                                                    <Button
                                                        type="text"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => remove(name)}
                                                        size="middle"
                                                        style={{ marginTop: isMobile ? 32 : 4 }}
                                                        disabled={isSpecLocked}
                                                    />
                                                </div>
                                            </Col>
                                        </Row>

                                        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', backgroundColor: '#fafafa', padding: 8, borderRadius: 6 }}>
                                            <Form.Item shouldUpdate noStyle>
                                                {({ getFieldValue }) => {
                                                    const item = getFieldValue(['items', index]) || {};
                                                    const taxable = Math.round((item.quantity || 0) * (item.rate || 0));
                                                    const gstVal = Math.round(taxable * (((item.cgstRate || 0) + (item.sgstRate || 0)) / 100));
                                                    return (
                                                        <Space split={<Divider type="vertical" />} wrap>
                                                            <Text type="secondary" style={{ fontSize: 12 }}>Taxable: ₹{taxable.toLocaleString()}</Text>
                                                            <Text type="secondary" style={{ fontSize: 12 }}>GST: ₹{gstVal.toLocaleString()}</Text>
                                                            <Text strong style={{ fontSize: 13 }}>Subtotal: ₹{(taxable + gstVal).toLocaleString()}</Text>
                                                        </Space>
                                                    );
                                                }}
                                            </Form.Item>
                                        </div>
                                    </div>
                                ))}
                                <Button
                                    type="dashed"
                                    onClick={() => add({ quantity: 1, rate: 0, cgstRate: 0, sgstRate: 0, natureKey: 'other' })}
                                    block
                                    icon={<PlusOutlined />}
                                    style={{ marginTop: 16, height: 45 }}
                                    disabled={isSpecLocked}
                                >
                                    Add Custom Item
                                </Button>
                            </>
                        )}
                    </Form.List>

                    <Card style={{ marginTop: 20, background: '#fafafa', borderRadius: 12 }} bodyStyle={{ padding: 12 }}>
                        <Form.Item shouldUpdate noStyle>
                            {({ getFieldValue }) => {
                                const items = getFieldValue('items') || [];
                                const total = items.reduce((sum, i) => {
                                    const taxVal = Math.round((i.quantity || 0) * (i.rate || 0) * (1 + ((i.cgstRate || 0) + (i.sgstRate || 0)) / 100));
                                    return sum + taxVal;
                                }, 0);
                                const diff = (orderValueFromForm || 0) - total;

                                return (
                                    <Row justify={isMobile ? "start" : "end"} align="middle" gutter={[16, 8]}>
                                        <Col xs={12} sm={10} md={6}>
                                            <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>Calculated Total</Text>
                                            <div style={{ fontSize: 18, fontWeight: 700 }}>₹ {total.toLocaleString()}</div>
                                        </Col>
                                        <Col xs={12} sm={10} md={6}>
                                            <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>Difference</Text>
                                            <div style={{ fontSize: 18, fontWeight: 700, color: diff === 0 ? '#52c41a' : '#ff4d4f' }}>
                                                {diff === 0 ? 'Balanced' : `₹ ${diff.toLocaleString()}`}
                                            </div>
                                        </Col>
                                    </Row>
                                );
                            }}
                        </Form.Item>
                    </Card>
                </div>
            )
        },
        {
            key: '2',
            label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FileSearchOutlined /> {isMobile ? "Status" : "Order Status"}
                    <Badge
                        count={completedCount}
                        style={{ backgroundColor: completedCount === 25 ? '#52c41a' : '#1890ff' }}
                        size="small"
                    />
                </span>
            ),
            children: (
                <div style={{ padding: isMobile ? '0' : '8px 4px' }}>
                    <Row gutter={[24, 24]}>
                        {milestoneGroups.map((group, idx) => (
                            <Col xs={24} lg={12} key={idx}>
                                <div style={{ marginBottom: 12 }}>
                                    <Title level={5} style={{
                                        margin: 0,
                                        paddingBottom: 8,
                                        borderBottom: '2px solid #1890ff',
                                        fontSize: 15,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}>
                                        <span>{group.label}</span>
                                        <Tag color="blue" style={{ margin: 0, borderRadius: 10 }}>
                                            {group.items.filter(i => isTaskCompleted(i.name)).length}/{group.items.length}
                                        </Tag>
                                    </Title>
                                </div>
                                <div style={{ maxHeight: isMobile ? 'none' : 500, overflowY: 'auto', paddingRight: 4 }}>
                                    {group.items.map((item, iIdx) => {
                                        const isJanSamarthItem =
                                            item.name === 'docsReceivedForJanSamarth' ||
                                            item.name === 'registrationOnJanSamarthDone';

                                        return (
                                            <MilestoneRow
                                                key={`${idx}-${iIdx}`}
                                                name={item.name}
                                                label={item.label}
                                                dateName={item.date}
                                                extraFieldName={item.extraField}
                                                extraFieldPlaceholder={item.placeholder}
                                                extraFieldRules={item.extraFieldRules}
                                                extraFieldNormalize={item.extraFieldNormalize}
                                                form={form}
                                                isActive={isTaskCompleted(item.name)}
                                                disabled={isJanSamarthItem && bankLoanYN === 'N'}
                                                skipDateValidation={item.skipDateValidation}
                                                isMobile={isMobile}
                                            />
                                        );
                                    })}
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
            )
        },
        {
            key: '3',
            label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <DoubleRightOutlined /> Finalize
                </span>
            ),
            children: (
                <div style={{ minHeight: isMobile ? 300 : 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '12px 0' : 0 }}>
                    <Card
                        style={{
                            maxWidth: 500,
                            width: '100%',
                            textAlign: 'center',
                            borderRadius: 16,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                        }}
                        bodyStyle={{ padding: isMobile ? 20 : 32 }}
                    >
                        <div style={{ marginBottom: 32 }}>
                            <CheckCircleFilled style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
                            <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>Ready to Finalize?</Title>
                            <Text type="secondary">Review the details and set the project status.</Text>
                        </div>

                        <div style={{ textAlign: 'left' }}>
                            <Form.Item label="Project Execution Status" name="projectStatus">
                                <Select size="large" variant="filled">
                                    <Option value="Open">Open - Awaiting Start</Option>
                                    <Option value="InProgress">In Progress - Execution</Option>
                                    <Option value="Completed">Completed - Grid Connected</Option>
                                    <Option value="Closed">Closed - Documented & Finalized</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item label="Total Contract Value (₹)" name="orderValue" rules={[{ required: true, message: 'Enter total contract value' }]}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    size="large"
                                    variant="filled"
                                    formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\₹\s?|(,*)/g, '')}
                                    disabled={isSpecLocked}
                                    min={0}
                                />
                            </Form.Item>
                        </div>

                        <div style={{
                            marginTop: 24,
                            padding: isMobile ? 12 : 20,
                            background: '#f6ffed',
                            borderRadius: 12,
                            border: '1px solid #b7eb8f',
                            display: 'flex',
                            gap: 12,
                            alignItems: 'flex-start',
                            textAlign: 'left'
                        }}>
                            <InfoCircleOutlined style={{ color: '#52c41a', marginTop: 4 }} />
                            <div>
                                <Text strong style={{ color: '#389e0d', display: 'block' }}>System Checkpoint</Text>
                                <Text style={{ fontSize: 13 }}>Current milestone completion is {Math.round((completedCount / 25) * 100)}%.</Text>
                            </div>
                        </div>
                    </Card>
                </div>
            )
        }
    ];

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={(changed, all) => {
                if (changed.orderValue !== undefined) {
                    const currentItems = all.items || [];
                    const orderVal = parseFloat(changed.orderValue) || 0;

                    const updatedItems = currentItems.map(item => {
                        if (item.natureKey && item.natureKey !== 'other') {
                            const opt = NATURE_OPTIONS.find(n => n.key === item.natureKey);
                            if (opt) {
                                let rate = 0;
                                if (item.natureKey === 'mseb') {
                                    rate = orderVal / 1.18;
                                } else {
                                    rate = (orderVal / 1.089) * (opt.percentOfTotal / 100);
                                }
                                return {
                                    ...item,
                                    rate: Math.round(rate),
                                    cgstRate: opt.cgst, // Ensure rates match nature
                                    sgstRate: opt.sgst,
                                    percentOfTotal: opt.percentOfTotal
                                };
                            }
                        }
                        return item;
                    });

                    form.setFieldsValue({ items: updatedItems });
                    // setTimeout here ensures setFieldsValue has processed before calculateTotals reads it
                    setTimeout(() => calculateTotals(), 0);
                } else if (changed.items) {
                    calculateTotals();
                }
            }}
            style={{ padding: '0 4px' }}
        >
            <div style={{
                marginBottom: 24,
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'flex-end',
                padding: '0 4px',
                gap: 12
            }}>
                <div>
                    <Title level={isMobile ? 4 : 3} style={{ margin: 0, color: '#002140' }}>
                        {order ? 'Refine Order Instance' : 'Order Registration'}
                    </Title>
                    <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>Lifecycle management and tracking for solar installations.</Text>
                </div>
                {order && (
                    <Tag color="blue" style={{ fontSize: 13, padding: '4px 12px', borderRadius: 6 }}>
                        <Text strong>Order ID: #{order.id}</Text>
                    </Tag>
                )}
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                size={isMobile ? "middle" : "large"}
                tabBarStyle={{ marginBottom: isMobile ? 16 : 32 }}
                animated={{ inkBar: true, tabs: true }}
            />

            {/* Spacer for mobile to prevent content being hidden under sticky footer */}
            {isMobile && <div style={{ height: 120 }} />}

            <div style={{
                marginTop: 24,
                textAlign: 'right',
                borderTop: '1px solid #f0f0f0',
                paddingTop: 24,
                background: '#fff',
                position: isMobile ? 'sticky' : 'static',
                bottom: 0,
                zIndex: 100,
                boxShadow: isMobile ? '0 -10px 20px rgba(0,0,0,0.08)' : 'none',
                margin: isMobile ? '0 -24px -24px' : 0,
                padding: isMobile ? '16px 24px' : '24px 0'
            }}>
                <Space size="middle" direction={isMobile ? "vertical" : "horizontal"} style={{ width: isMobile ? '100%' : 'auto' }}>
                    <Button onClick={onCancel} icon={<CloseOutlined />} size="large" block={isMobile}>Discard Changes</Button>
                    <Button
                        type="primary"
                        loading={loading}
                        icon={activeTab === '3' ? <SaveOutlined /> : <DoubleRightOutlined />}
                        size="large"
                        style={{ height: isMobile ? 45 : 38, padding: '0 22px', borderRadius: 8 }}
                        block={isMobile}
                        onClick={async () => {
                            try {
                                if (activeTab !== '3') {
                                    // Get fields of current tab to validate
                                    let fieldsToValidate = [];
                                    if (activeTab === '1') fieldsToValidate = ['customerId', 'selectedConsumerNumber', 'projectType', 'plantSizeKw', 'orderDate'];
                                    if (activeTab === 'items_tab') fieldsToValidate = ['orderValue', 'items'];
                                    // Tab 2 has dynamic milestone fields - might be better to validate all rendered for now

                                    await form.validateFields(fieldsToValidate.length > 0 ? fieldsToValidate : undefined);

                                    const currentIndex = tabSequence.indexOf(activeTab);
                                    if (currentIndex < tabSequence.length - 1) {
                                        setActiveTab(tabSequence[currentIndex + 1]);
                                    }
                                } else {
                                    form.submit();
                                }
                            } catch (err) {
                                notification.error({
                                    message: 'Validation Failed',
                                    description: 'Please correct the errors before proceeding.'
                                });
                            }
                        }}
                    >
                        {activeTab === '3' ? (order ? 'Commit Updates' : 'Initialize Project') : 'Continue to Next Step'}
                    </Button>
                </Space>
            </div>
        </Form>
    );
};

export default OrderForm;
